'use client';

import { useEffect, useMemo, useState } from 'react';

import RichText from '@/components/ui/RichText/RichText';
import type { WhatIsYourConcernSection } from '@/features/ultrasound/data/ultrasound.types';

import ScanCard from './ScanCard';
import ScanCategoryTabs from './ScanCategoryTabs';

type Props = {
  data: WhatIsYourConcernSection;
};

const MOBILE_PAGE_SIZE = 6;

const ScanFinderSection = ({ data }: Props) => {
  const concerns = useMemo(() => data?.cards || [], [data]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(MOBILE_PAGE_SIZE);

  useEffect(() => {
    setVisibleCount(MOBILE_PAGE_SIZE);
  }, [activeIndex]);

  if (!concerns.length) return null;

  const activeConcern = concerns[activeIndex] ?? concerns[0];
  const treatments = activeConcern?.treatments ?? [];
  const hasMore = visibleCount < treatments.length;

  return (
    <section
      id="scan-finder"
      data-testid="scan-finder-section"
      className="py-8 md:py-10"
    >
      <div className="content-wrapper mb-16 md:mb-20">
        <div className="mx-auto max-w-4xl text-center">
          <RichText styles={data.heading.styles}>
            {data.heading.content}
          </RichText>
          <RichText styles={data.description.styles}>
            {data.description.content}
          </RichText>
        </div>
      </div>

      <div className="bg-surface-light mt-10 py-8 md:mt-12 md:py-10">
        <div className="content-wrapper">
          <div className="-mt-18 mb-6 md:-mt-22">
            <ScanCategoryTabs
              concerns={concerns}
              activeIndex={activeIndex}
              onSelect={setActiveIndex}
            />
          </div>

          <RichText styles={activeConcern.sectionTitle.styles}>
            {activeConcern.sectionTitle.content}
          </RichText>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {treatments.map((treatment, index) => (
              <div
                key={index}
                className={
                  index >= visibleCount ? 'tablet:block hidden' : undefined
                }
              >
                <ScanCard treatment={treatment} />
              </div>
            ))}
          </div>

          {hasMore && (
            <div className="tablet:hidden mt-8 flex justify-center">
              <button
                type="button"
                onClick={() =>
                  setVisibleCount((count) => count + MOBILE_PAGE_SIZE)
                }
                className="border-info-500-soft text-text-action-teal hover:bg-info-500-soft flex cursor-pointer items-center gap-2 rounded-lg border px-4 py-2.5 text-base font-bold transition"
              >
                View More
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                  className="h-4 w-4"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ScanFinderSection;
